import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { DateTime as LuxonDateTime } from 'luxon';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext } from 'src/shared/util/secondary-page-context';

import { BankRowsService } from 'src/shared/services/bank-rows.service';
import {
  BankTransaction,
  FinancialConnectionsAccount,
  FinancialConnectionsService,
} from 'src/shared/services/financial-connections.service';
import { FinancialTransaction, PropertyTransactionsService } from 'src/shared/services/property-transactions.service';

import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface BankTransactionsParams {
  connectionId?: number;
  connection?: FinancialConnectionsAccount;
}

type StatusFilter = 'all' | 'pnl' | 'unknown' | 'skip' | 'unparsed' | 'removed';
type RowStatus = Exclude<StatusFilter, 'all'>;

interface BankRowView {
  row: BankTransaction;
  formattedAmount: string;
  /** What the row is, derived once so the label and the status filter can never disagree. */
  status: RowStatus;
  statusLabel: string;
  statusDetail: string;
  inPnl: boolean;
  /** Skipped, removed, or otherwise eligible for "Add to your P&L". Unparsed and voided rows are not. */
  canToggle: boolean;
  pnlTransaction: FinancialTransaction | null;
}

const PAGE_SIZE = 50;

/**
 * The raw rows of one bank account with how each was classified. Rows in the P&L
 * open their transaction; rows left out get an "Add to your P&L" action.
 */
@Component({
  templateUrl: 'bank-transactions.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BankTransactionsPage implements OnInit {
  private readonly ufb = inject(UntypedFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly bankRows = inject(BankRowsService);
  private readonly financialConnections = inject(FinancialConnectionsService);
  private readonly propertyTransactions = inject(PropertyTransactionsService);
  readonly windowService = inject(WindowService);

  readonly statusItems: SelectModel[] = [
    { value: 'all', viewValue: 'All statuses' },
    { value: 'pnl', viewValue: 'In P&L' },
    { value: 'unknown', viewValue: 'Needs review' },
    { value: 'skip', viewValue: 'Skipped' },
    { value: 'removed', viewValue: 'Removed from P&L' },
    { value: 'unparsed', viewValue: 'Not parsed yet' },
  ];
  readonly dateRangeOptions = [
    { viewValue: 'Custom', value: 'custom' },
    { viewValue: 'Last 30 Days', value: 'last_30_days' },
    { viewValue: 'Last 3 Months', value: 'last_3_months' },
    { viewValue: 'This Year', value: 'this_year' },
    { viewValue: 'All Time', value: 'all_time' },
  ];
  dateRangeForm: UntypedFormGroup = this.ufb.group({ start: '', end: '', customStringSelect: '' });
  statusFilter: StatusFilter = 'all';

  isRightSideContent = true;
  loaded = false;
  loadingMore = false;
  hasMore = false;
  errorMessage = '';
  connectionId: number | null = null;
  connection: FinancialConnectionsAccount | null = null;
  connectionLabel = 'Bank Transactions';
  searchText = '';
  dateFrom: string | null = null;
  dateTo: string | null = null;
  allRows: BankTransaction[] = [];
  livePnlByBankRowId = new Map<number, FinancialTransaction>();
  views: BankRowView[] = [];

  readonly boundLoadMore = () => this.loadMore();

  private page = 1;

  async ngOnInit() {
    const { isRightSideContent, params } = await readSecondaryPageContext<BankTransactionsParams>(this.storage);
    this.isRightSideContent = isRightSideContent;
    this.connection = params.connection ?? null;
    this.connectionId = params.connectionId ?? this.connection?.id ?? null;
    if (this.connection) {
      this.connectionLabel = this.bankRows.connectionLabel(this.connection);
    }
    this.rightSidePanelService.setDialogPageTitle(this.connectionLabel);
    await this.loadPage();
  }

  ionViewWillEnter() {
    if (this.loaded) {
      this.loadPage();
    }
  }

  async loadPage(): Promise<void> {
    if (!this.connectionId) {
      this.errorMessage = 'Bank account not found.';
      this.loaded = true;
      this.cdr.markForCheck();
      return;
    }
    this.page = 1;
    this.errorMessage = '';
    try {
      const [rows, liveMap] = await Promise.all([
        this.fetchPage(1),
        this.bankRows.loadLivePnlRowsByBankRowId(),
      ]);
      this.allRows = rows;
      this.livePnlByBankRowId = liveMap;
      this.hasMore = rows.length === PAGE_SIZE;
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load bank transactions';
      this.allRows = [];
    }
    this.loaded = true;
    this.rebuild();
  }

  async loadMore(): Promise<void> {
    if (this.loadingMore || !this.hasMore) { return; }
    this.loadingMore = true;
    this.cdr.markForCheck();
    try {
      const rows = await this.fetchPage(this.page + 1);
      this.page += 1;
      this.allRows = [...this.allRows, ...rows];
      this.hasMore = rows.length === PAGE_SIZE;
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to load more transactions');
    } finally {
      this.loadingMore = false;
      this.rebuild();
    }
  }

  private fetchPage(page: number): Promise<BankTransaction[]> {
    return this.financialConnections.getBankTransactions(this.connectionId, {
      page,
      per_page: PAGE_SIZE,
      date_from: this.dateFrom ?? undefined,
      date_to: this.dateTo ?? undefined,
    });
  }

  onSearch(value: string): void {
    this.searchText = (value || '').trim().toLowerCase();
    this.rebuild();
  }

  setStatusFilter(value: string): void {
    this.statusFilter = (value || 'all') as StatusFilter;
    this.rebuild();
  }

  selectStringDateRange(selection: string | null): void {
    const today = LuxonDateTime.local();
    let start: LuxonDateTime | null = null;
    switch (selection) {
      case 'last_30_days': start = today.minus({ days: 30 }); break;
      case 'last_3_months': start = today.minus({ months: 3 }); break;
      case 'this_year': start = today.startOf('year'); break;
      default: start = null;
    }
    this.dateFrom = start ? start.toFormat('yyyy-MM-dd') : null;
    this.dateTo = start ? today.toFormat('yyyy-MM-dd') : null;
    this.loadPage();
  }

  selectCustomDateRange(range: UntypedFormGroup | { start: Date; end: Date } | null): void {
    const value: any = range && 'value' in range ? range.value : range;
    if (!value?.start || !value?.end) { return; }
    this.dateFrom = LuxonDateTime.fromJSDate(new Date(value.start)).toFormat('yyyy-MM-dd');
    this.dateTo = LuxonDateTime.fromJSDate(new Date(value.end)).toFormat('yyyy-MM-dd');
    this.loadPage();
  }

  private rebuild(): void {
    this.views = this.allRows
      .map(row => this.toView(row))
      .filter(view => this.matchesStatus(view, this.statusFilter))
      .filter(view => !this.searchText || (view.row.description || '').toLowerCase().includes(this.searchText));
    this.cdr.markForCheck();
  }

  private matchesStatus(view: BankRowView, filter: StatusFilter): boolean {
    return filter === 'all' || view.status === filter;
  }

  private toView(row: BankTransaction): BankRowView {
    const pnlTransaction = this.livePnlByBankRowId.get(row.id) ?? null;
    const inPnl = !!pnlTransaction;
    const by = row.classified_by === 'rule' ? 'by a rule' : row.classified_by === 'llm' ? 'by TIDY AI' : row.classified_by === 'customer' ? 'by you' : '';
    let status: RowStatus = 'unparsed';
    let statusLabel = 'Not parsed yet';
    let statusDetail = 'Waiting for the next sync';
    if (row.classification === 'skip') {
      status = 'skip';
      statusLabel = 'Skipped';
      statusDetail = by;
    } else if (row.classification === 'pnl' || row.classification === 'unknown') {
      if (!inPnl) {
        status = 'removed';
        statusLabel = 'Removed from P&L';
        statusDetail = '';
      } else if (pnlTransaction.monthly_finance_type && pnlTransaction.address_id) {
        status = 'pnl';
        statusLabel = pnlTransaction.monthly_finance_type.label;
        statusDetail = by;
      } else {
        // A row a rule put in the P&L without a property lands here too, not just
        // an `unknown` one, so the filter has to follow the label rather than the
        // raw classification.
        status = 'unknown';
        statusLabel = 'Needs review';
        statusDetail = pnlTransaction.monthly_finance_type ? 'Missing property' : (by ? `Uncategorized, ${by}` : 'Uncategorized');
      }
    }
    return {
      row,
      formattedAmount: this.propertyTransactions.formatAmount(row),
      status,
      statusLabel,
      statusDetail,
      inPnl,
      canToggle: row.classification !== null && row.status !== 'void',
      pnlTransaction,
    };
  }

  openRow(view: BankRowView): void {
    if (!view.inPnl) { return; }
    this.rightSidePanelService.navigateTo('transaction-detail', {
      transaction: view.pnlTransaction,
      addToPnl: null,
      connectionLabel: this.connectionLabel,
    });
  }

  openAddToPnl(view: BankRowView): void {
    this.rightSidePanelService.navigateTo('transaction-detail', {
      transaction: null,
      addToPnl: { bankTransaction: view.row, connectionId: this.connectionId, connectionLabel: this.connectionLabel },
    });
  }

  createRule(view: BankRowView): void {
    const live = view.pnlTransaction;
    this.rightSidePanelService.navigateTo('transaction-rule-form', {
      rule: null,
      sample: { description: view.row.description, transaction_type: view.row.transaction_type },
      suggestedApplies: view.row.classification === 'skip'
        ? { classification: 'skip' }
        : {
          classification: 'pnl',
          monthly_finance_type_key: live?.monthly_finance_type?.key,
          address_id: live?.address_id ?? undefined,
        },
    });
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
